import { useState, useEffect, useCallback } from "react";
import { backendGet, backendPost } from "@accredo/fe-shared/services/dataService.js";

export const normaliseAttachment = (raw) => ({
  id: raw.id,
  uuid: raw.uuid ?? null,
  name: raw.filename ?? "Unnamed file",
  description: raw.bezeichnung ?? null,
  used_in: raw.used_in ?? [],
  size: raw.filesize ?? null,
  mimetype: raw.mimetype ?? null,
  url: raw.download_url ?? null,
  serverPreviewUrl: raw.preview_url ?? null,
  mailOptimizedSize: raw.mail_optimized_filesize ?? null,
  isSession: false,
});

/**
 * Upload a single file as a claim attachment (Anlage) for the given claim.
 *
 * Called by useSessionFiles.addSessionFile during file selection to immediately
 * upload the file. Returns a normalised file record (including the server `id`)
 * so the picker can store the real server ID in the RHF field right away.
 *
 * When claimId is omitted the file is uploaded as an orphan attachment
 * (schaden_id null) and linked to the claim later on first save.
 *
 * @param {number|string|null} claimId
 * @param {File} file
 * @param {string} uuid - Client-generated UUID for backend ownership verification
 * @param {string|null} description - Optional description/label (bezeichnung) for the file
 * @returns {Promise<{id:number,name:string,description:?string,size:?number,mimetype:?string,url:?string,isSession:false}>}
 */
export const uploadClaimAttachment = async (
  claimId,
  file,
  uuid,
  description = null
) => {
  const formData = new FormData();

  formData.append("file", file);
  formData.append("uuid", uuid);
  if (claimId) {
    formData.append("schaden_id", String(claimId));
  }
  if (description) {
    formData.append("bezeichnung", description);
  }

  const result = await backendPost("data/anlages/upload", {
    toSend: formData,
    options: { headers: { "Content-Type": "multipart/form-data" } },
    throwOnFailure: true,
  });

  return normaliseAttachment(result?.data ?? result);
};

export function useClaimAttachmentFiles({ claimId }) {
  const [files, setFiles] = useState([]);
  const [isLoading, setIsLoading] = useState(false);
  const [error, setError] = useState(null);
  const [revision, setRevision] = useState(0);

  const refetch = useCallback(() => setRevision((r) => r + 1), []);

  useEffect(() => {
    if (!claimId) {
      setFiles([]);
      return;
    }

    let cancelled = false;
    setIsLoading(true);
    setError(null);

    backendGet(`/data/anlages?filter[schaden_id]=${claimId}`, {
      setResult: (raw) => {
        if (!cancelled) {
          setFiles((raw?.data ?? []).map(normaliseAttachment));
        }
      },
      setIsLoading: setIsLoading,
    });

    return () => {
      cancelled = true;
    };
  }, [claimId, revision]);

  return { files, isLoading, error, refetch };
}
